/* eslint-disable no-shadow,no-use-before-define */
import React, { useState } from "react";
import "./TenantsAdmin.less";
import { Row, Button, Input, Tooltip, Table, Form } from "antd";
import { RootStateOrAny, useSelector } from "react-redux";
import {
  tenantsAdminData,
  tenantsData,
  tenantsDataUpdate,
  tenantsDataWithKey,
} from "./TenantsAdminInterfaces";
import tickIcon from "../../img/done.svg";
import deleteIcon from "../../img/delete-round.svg";
import edit from "../../img/edit.svg";
import { checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { createUpdateTenants, fetchTenant } from "../../store/slices/tenants-admin";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import LocalStorageService from "../../services/LocalStorageService";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ADMIN_TENANT_WRITE } from "../../lib/const/permissions";
import { useAppDispatch } from "../..";

const TenantsAdmin: React.FC<tenantsAdminData> = ({ tenant, tenantLoading }) => {
  const dispatch = useAppDispatch();
  const { dicCurrencies } = useSelector((state: RootStateOrAny) => state.rtkDictionaries);
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const isEditing = (rec: tenantsData) => rec.key === editingKey;
  const [editStatus, setEditStatus] = useState<string>("");
  const isEditingForce = editStatus === "editingForce";
  const [obj, setObj] = useState<tenantsData | null>(null);
  const [dataUpdate, setDataUpdate] = useState<tenantsDataUpdate>({
    name: "",
    dsc: "",
  });

  const [form] = Form.useForm();

  const tableData = tenant
    ? [
        {
          ...tenant,
          key: tenant?.id,
          defaultCurrencyName: dicCurrencies.find((item) => item.id === tenant?.defaultCurrencyId)
            ?.name,
        },
      ]
    : [];

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: "10%",
    },
    {
      title: "Наименование",
      dataIndex: "name",
      key: "name",
      width: "20%",
      render: (val: string, record: tenantsData) => {
        const editable = isEditing(record);
        const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
          setDataUpdate({ ...dataUpdate, name: e.target.value });
        };
        return editable && isEditingForce ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={val}
            name="name"
            rules={[{ required: true, message: "" }]}
          >
            <Input onChange={onChangeValue} />
          </Form.Item>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "Временная зона",
      dataIndex: "defaultTimezoneName",
      key: "defaultTimezoneName",
      width: "15%",
    },
    {
      title: "Часовой пояс",
      dataIndex: "defaultTimezone",
      key: "defaultTimezone",
      width: "15%",
    },
    {
      title: "Валюта",
      dataIndex: "defaultCurrencyName",
      key: "defaultCurrencyName",
      width: "20%",
    },
    {
      title: "Комментарий",
      dataIndex: "dsc",
      key: "dsc",
      width: "20%",
      render: (val: string, record: tenantsData) => {
        const editable = isEditing(record);
        const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
          setDataUpdate({ ...dataUpdate, dsc: e.target.value });
        };
        return editable && isEditingForce ? (
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            name="dsc"
            initialValue={val}
            rules={[{ required: true, message: "" }]}
          >
            <Input onChange={onChangeValue} />
          </Form.Item>
        ) : (
          <>{val}</>
        );
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "actions",
      render: (record: tenantsDataWithKey) => {
        const tenantId = Number(LocalStorageService.getTenantId());
        const editable = isEditing(record);
        const disabled = isEditingForce;

        const handleEdit = (editStatus) => {
          form.resetFields();
          setEditingKey(() => record.key);
          setEditStatus(editStatus);
          setDataUpdate({ ...dataUpdate, name: record.name, dsc: record.dsc });
        };

        const handleCancel = () => {
          form.resetFields();
          setEditStatus("");
          setEditingKey(null);
        };

        const handleSubmit = () => {
          form.validateFields().then(() => {
            if (isEditingForce) {
              dispatch(
                createUpdateTenants({
                  id: record.id,
                  name: dataUpdate.name,
                  dsc: dataUpdate.dsc,
                  unidentifiedSubscriberId: record.unidentifiedSubscriberId,
                  defaultTimezone: record.defaultTimezone,
                  allowedTaxMask: record.allowedTaxMask,
                  defaultCurrencyId: record.defaultCurrencyId,
                })
              )
                .then((res) => {
                  if (res.payload !== undefined) {
                    dispatch(fetchTenant(tenantId));
                  }
                })
                .finally(() => {
                  setEditingKey(null);
                  setEditStatus("");
                  form.resetFields();
                });
            }
          });
        };

        return editable && isEditingForce ? (
          <>
            <Tooltip title="Подтвердить изменения">
              <Button
                icon={<img src={tickIcon} alt="tick" width="16px" />}
                className="icon"
                type="text"
                htmlType="button"
                onClick={handleSubmit}
              />
            </Tooltip>
            <Tooltip title="Отменить">
              <Button
                icon={<img src={deleteIcon} width="16px" alt="delete" />}
                type="text"
                className="icon"
                htmlType="button"
                onClick={() => handleCancel()}
              />
            </Tooltip>
          </>
        ) : (
          <>
            {hasAccess(MENU_ADMIN_TENANT_WRITE) && (
              <Tooltip title="Редактировать">
                <Button
                  icon={<img src={edit} alt="pencil" width="16px" />}
                  type="text"
                  htmlType="button"
                  disabled={disabled}
                  onClick={() => handleEdit("editingForce")}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      dataIndex: "",
      key: "button",
      width: "2%",
      render: (record: tenantsData) => {
        const handleShowModal = (obj) => {
          setObj(obj);
        };
        return (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleShowModal(record);
            }}
            className="button-icon iconButton"
            style={{ outline: "none" }}
            icon={<DotsIcon />}
          />
        );
      },
    },
  ];

  const handleHideModal = () => {
    setObj(null);
  };

  return (
    <>
      <div className="inner-row" style={{ marginBottom: "15px" }}>
        <Row align="middle" justify="space-between">
          <h1>Тенант</h1>
          {/* <Col>
            <Link to="tenants/create">
              <Button type="primary">Добавить тенант</Button>
            </Link>
          </Col> */}
        </Row>

        <Form form={form}>
          <Table
            size="small"
            className="tenant-table version-inner-table"
            columns={columns}
            dataSource={tableData}
            pagination={false}
            loading={checkIsLoading(tenantLoading)}
          />
        </Form>

        <HistoryAndPropertyModal
          objectTypeSysname="tenant"
          objectId={obj?.id || 0}
          handleHideModal={handleHideModal}
          visibleModal={Boolean(obj?.id)}
          hasAccessToEdit={hasAccess(MENU_ADMIN_TENANT_WRITE)}
        />
      </div>
    </>
  );
};

export default TenantsAdmin;
